import { Injectable } from '@angular/core';
import { io, type Socket } from 'socket.io-client';
import { Subject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import type { ApiMessage, HandoffRow } from '../api/api.types';

export type SocketConnectEvent = {
  isReconnect: boolean;
  rejoinedConversationIds: string[];
};

export type JoinConversationAck = { ok: true; room: string };

export type SendMessageAck = {
  ok: true;
  messageId: string;
  conversationId: string;
  clientMessageId?: string;
};

export type ConversationUpdatedEvent = {
  conversationId: string;
  mode?: 'AI' | 'HUMAN' | 'HYBRID';
  assignedAdminId?: string | null;
  status?: 'OPEN' | 'CLOSED';
};

export type HandoffUpsertedEvent = { handoff: HandoffRow };

export type HandoffTakenEvent = {
  handoffId: string;
  conversationId: string;
  takenByAdminId: string | null;
  takenAt: string | null;
};

export type TypingStateChangedEvent = {
  conversationId: string;
  actor: 'USER' | 'OPERATOR';
  isTyping: boolean;
};

export type OrderStatusChangedEvent = {
  orderId: string;
  conversationId: string;
  status: string;
};

export type OrderUpdatedEvent = {
  orderId: string;
  conversationId: string;
};

@Injectable({ providedIn: 'root' })
export class OperatorSocketService {
  private socket: Socket | null = null;
  private connectedOnce = false;
  private browserLifecycleBound = false;
  private authRecoveryInFlight: Promise<boolean> | null = null;
  private readonly joinedConversationIds = new Set<string>();

  private readonly connectedSubject = new Subject<SocketConnectEvent>();
  private readonly messageCreatedSubject = new Subject<ApiMessage>();
  private readonly conversationUpdatedSubject = new Subject<ConversationUpdatedEvent>();
  private readonly handoffUpsertedSubject = new Subject<HandoffUpsertedEvent>();
  private readonly handoffTakenSubject = new Subject<HandoffTakenEvent>();
  private readonly typingStateChangedSubject = new Subject<TypingStateChangedEvent>();
  private readonly orderStatusChangedSubject = new Subject<OrderStatusChangedEvent>();
  private readonly orderUpdatedSubject = new Subject<OrderUpdatedEvent>();

  readonly connected$ = this.connectedSubject.asObservable();
  readonly messageCreated$ = this.messageCreatedSubject.asObservable();
  readonly conversationUpdated$ = this.conversationUpdatedSubject.asObservable();
  readonly handoffUpserted$ = this.handoffUpsertedSubject.asObservable();
  readonly handoffTaken$ = this.handoffTakenSubject.asObservable();
  readonly typingStateChanged$ = this.typingStateChangedSubject.asObservable();
  readonly orderStatusChanged$ = this.orderStatusChangedSubject.asObservable();
  readonly orderUpdated$ = this.orderUpdatedSubject.asObservable();

  constructor(private readonly auth: AuthService) {
    this.bindBrowserLifecycle();
  }

  async connect(): Promise<void> {
    await this.auth.whenReady();
    const accessToken = this.auth.getAccessToken();
    if (!accessToken) throw new Error('Not authenticated (no access token)');

    this.ensureSocket();
    this.applyLatestAuthToken();

    if (!this.socket?.connected) {
      this.socket?.connect();
    }

    try {
      await this.waitForConnect();
    } catch (error) {
      if (this.isUnauthenticatedConnectError(error)) {
        const recovered = await this.recoverSocketAuthentication('connect');
        if (recovered) {
          await this.waitForConnect();
          return;
        }
      }
      throw error;
    }
  }

  async joinConversation(conversationId: string): Promise<JoinConversationAck | void> {
    const normalizedId = conversationId.trim();
    if (!normalizedId) return;

    this.joinedConversationIds.add(normalizedId);
    if (!this.socket?.connected) return;

    return this.emitWithAck<JoinConversationAck>('joinConversation', { conversationId: normalizedId });
  }

  leaveConversation(conversationId: string): void {
    const normalizedId = conversationId.trim();
    if (!normalizedId) return;
    this.joinedConversationIds.delete(normalizedId);
    // Tell the server to remove this socket from the room so events stop arriving.
    this.emit('leaveConversation', { conversationId: normalizedId });
  }

  getJoinedConversationIds(): string[] {
    return [...this.joinedConversationIds];
  }

  async sendMessage(payload: {
    text: string;
    clientMessageId?: string;
    conversationId: string;
    attachmentIds?: string[];
  }): Promise<SendMessageAck> {
    return this.emitWithAck<SendMessageAck>('sendMessage', payload);
  }

  sendTypingState(payload: { conversationId: string; isTyping: boolean }): void {
    this.emit('typingState', payload);
  }

  emit(event: string, payload?: any): void {
    if (!this.socket?.connected) return;
    this.socket.emit(event, payload ?? {});
  }

  disconnectAndReset(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectedOnce = false;
    this.authRecoveryInFlight = null;
    this.joinedConversationIds.clear();
  }

  private ensureSocket(): void {
    if (this.socket) return;

    this.socket = io(environment.apiBaseUrl, {
      auth: { token: this.formatBearerToken(this.auth.getAccessToken()) },
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
    });

    this.socket.on('connect', () => {
      void this.handleSocketConnected();
    });

    this.socket.on('messageCreated', (msg: ApiMessage) => {
      this.messageCreatedSubject.next(msg);
    });

    this.socket.on('conversationUpdated', (ev: ConversationUpdatedEvent) => {
      this.conversationUpdatedSubject.next(ev);
    });

    this.socket.on('handoffUpserted', (ev: HandoffUpsertedEvent) => {
      this.handoffUpsertedSubject.next(ev);
    });

    this.socket.on('handoffTaken', (ev: HandoffTakenEvent) => {
      this.handoffTakenSubject.next(ev);
    });

    this.socket.on('typingStateChanged', (ev: TypingStateChangedEvent) => {
      this.typingStateChangedSubject.next(ev);
    });

    this.socket.on('orderStatusChanged', (ev: OrderStatusChangedEvent) => {
      this.orderStatusChangedSubject.next(ev);
    });

    this.socket.on('orderUpdated', (ev: OrderUpdatedEvent) => {
      this.orderUpdatedSubject.next(ev);
    });

    this.socket.io.on('reconnect_attempt', () => {
      this.applyLatestAuthToken();
    });

    this.socket.on('connect_error', (err: unknown) => {
      console.error('[OperatorSocket] connect_error:', err);
      if (this.isUnauthenticatedConnectError(err)) {
        void this.recoverSocketAuthentication('connect_error');
      }
    });

    this.socket.on('disconnect', (reason: unknown) => {
      console.warn('[OperatorSocket] disconnected:', reason);
    });
  }

  private async handleSocketConnected(): Promise<void> {
    const isReconnect = this.connectedOnce;
    this.connectedOnce = true;
    const rejoinedConversationIds = await this.replayJoinedConversations();
    this.connectedSubject.next({ isReconnect, rejoinedConversationIds });
  }

  private async replayJoinedConversations(): Promise<string[]> {
    const ids = [...this.joinedConversationIds];
    if (!ids.length || !this.socket?.connected) return [];

    const rejoined: string[] = [];

    for (const conversationId of ids) {
      try {
        await this.emitWithAck<JoinConversationAck>('joinConversation', { conversationId });
        rejoined.push(conversationId);
      } catch (error) {
        console.warn(`[OperatorSocket] failed to rejoin conversation ${conversationId}`, error);
      }
    }

    return rejoined;
  }

  private formatBearerToken(token: string | null): string {
    return token ? `Bearer ${token}` : '';
  }

  private applyLatestAuthToken(): void {
    const token = this.auth.getAccessToken();
    if (!token || !this.socket) return;
    this.socket.auth = { token: this.formatBearerToken(token) };
  }

  private isUnauthenticatedConnectError(error: unknown): boolean {
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : typeof (error as any)?.message === 'string'
            ? String((error as any).message)
            : '';

    return message.trim().toLowerCase() === 'unauthenticated';
  }

  private async recoverSocketAuthentication(reason: string): Promise<boolean> {
    if (this.authRecoveryInFlight) return this.authRecoveryInFlight;

    const attempt = (async () => {
      try {
        const refreshed = await this.auth.refreshSession();
        if (!refreshed) return false;

        this.applyLatestAuthToken();
        if (this.socket && !this.socket.connected) {
          this.socket.connect();
        }
        return true;
      } catch (error) {
        console.warn(`[OperatorSocket] auth recovery failed (${reason})`, error);
        return false;
      }
    })();

    this.authRecoveryInFlight = attempt.finally(() => {
      this.authRecoveryInFlight = null;
    });

    return this.authRecoveryInFlight;
  }

  private bindBrowserLifecycle(): void {
    if (
      this.browserLifecycleBound ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    this.browserLifecycleBound = true;

    const reconnectIfNeeded = () => {
      if (!this.socket || this.socket.connected) return;
      void this.connect().catch((error) => {
        console.warn('[OperatorSocket] browser lifecycle reconnect failed', error);
      });
    };

    window.addEventListener('online', reconnectIfNeeded);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        reconnectIfNeeded();
      }
    });
  }

  private waitForConnect(timeoutMs = 8000): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = this.socket;
      if (!s) return reject(new Error('Socket not initialized'));
      if (s.connected) return resolve();

      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('WS connect timeout'));
      }, timeoutMs);

      const onConnect = () => {
        cleanup();
        resolve();
      };

      const onError = (err: unknown) => {
        cleanup();
        reject(err instanceof Error ? err : new Error('connect_error'));
      };

      const cleanup = () => {
        clearTimeout(timer);
        s.off('connect', onConnect);
        s.off('connect_error', onError);
      };

      s.once('connect', onConnect);
      s.once('connect_error', onError);
    });
  }

  private emitWithAck<T>(event: string, payload: unknown, timeoutMs = 8000): Promise<T> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket?.connected) return reject(new Error('Socket not connected'));

      socket.timeout(timeoutMs).emit(event, payload, (err: unknown, resp: T) => {
        if (err) return reject(err instanceof Error ? err : new Error(String(err)));
        resolve(resp);
      });
    });
  }
}
